"use client"

import React, { useState, useRef, useEffect } from 'react'
import Image from 'next/image'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { Label } from '@/shared/components/ui/label'
import { Textarea } from '@/shared/components/ui/textarea'
import { Play, FileText, Mic } from 'lucide-react'
import VoiceRecordingModal from '@/features/photos/components/voice-recording-modal'
import { useUploadQueue } from '@/features/photos/hooks/use-upload-queue'
import successToast from '@/shared/lib/toast/success.toast'
import errorToast from '@/shared/lib/toast/error.toast'
import { authFetch } from '@/shared/lib/auth-fetch'

const EMPTY_FILE_ARRAY: File[] = []

function FilePreviewItem({
  file,
  isVideo,
  isPdf,
  onRemove,
}: {
  file: File
  isVideo: boolean
  isPdf?: boolean
  onRemove: () => void
}) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  useEffect(() => {
    const url = URL.createObjectURL(file)
    setPreviewUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [file])

  if (!previewUrl && !isPdf) return null

  return (
    <div className="relative group">
      {isPdf ? (
        <div className="w-full h-20 rounded border overflow-hidden bg-muted flex items-center justify-center gap-2 p-2">
          <FileText className="w-8 h-8 text-muted-foreground" />
          <span className="text-xs truncate">{file.name}</span>
        </div>
      ) : isVideo ? (
        <div className="relative w-full h-20 rounded border overflow-hidden bg-black">
          <video src={previewUrl!} className="w-full h-full object-cover" muted />
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
            <Play className="w-8 h-8 text-white fill-white" />
          </div>
        </div>
      ) : (
        <Image
          src={previewUrl!}
          alt={`Preview ${file.name}`}
          className="w-full h-20 object-cover rounded border"
          width={80}
          height={80}
        />
      )}
      <button
        onClick={onRemove}
        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600 z-10"
      >
        ×
      </button>
    </div>
  )
}

interface AddBasePhotoModalProps {
  isOpen: boolean
  onClose: () => void
  regionId: number
  onUploadSuccess?: () => void
  initialFiles?: File[]
  componentName?: string
}

export default function AddBasePhotoModal({
  isOpen,
  onClose,
  regionId,
  onUploadSuccess,
  initialFiles = EMPTY_FILE_ARRAY,
  componentName = 'Base Photos',
}: AddBasePhotoModalProps) {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [description, setDescription] = useState('')
  const [isVoiceModalOpen, setIsVoiceModalOpen] = useState(false)
  const galleryInputRef = useRef<HTMLInputElement>(null)
  const submittedRef = useRef(false)

  const { enqueueBasePhotos } = useUploadQueue()

  // Reset form state on open/close
  useEffect(() => {
    if (isOpen && initialFiles.length > 0) {
      setSelectedFiles(initialFiles)
    }
    if (!isOpen) {
      if (initialFiles.length === 0) {
        setSelectedFiles([])
      }
    }
  }, [isOpen, initialFiles])

  const showDescription = selectedFiles.length === 1

  useEffect(() => {
    if (!showDescription) {
      setDescription('')
      setIsVoiceModalOpen(false)
    }
  }, [showDescription])

  const validateFile = (file: File): string | null => {
    const fileName = file.name.toLowerCase()
    const lastDot = fileName.lastIndexOf('.')
    const fileExtension = lastDot >= 0 ? fileName.substring(lastDot) : ''

    const unsupportedVideoExtensions = ['.avi', '.mkv', '.flv', '.wmv', '.3gp', '.divx', '.xvid']
    if (unsupportedVideoExtensions.includes(fileExtension)) {
      return `Video "${file.name}" format is not supported. Please convert to MP4, MOV, WebM, or M4V format.`
    }
    const unsupportedVideoMimeTypes = [
      'video/x-msvideo',
      'video/x-matroska',
      'video/x-flv',
      'video/x-ms-wmv',
      'video/3gpp',
    ]
    if (unsupportedVideoMimeTypes.includes(file.type.toLowerCase())) {
      return `Video "${file.name}" format is not supported. Please convert to MP4, MOV, WebM, or M4V format.`
    }
    const isImage = file.type.startsWith('image/')
    const isVideo = file.type.startsWith('video/')
    const isPdf = file.type === 'application/pdf' || fileExtension === '.pdf'
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.tiff', '.heic', '.heif']
    const videoExtensions = ['.mov', '.mp4', '.webm', '.m4v', '.ts']
    const isImageByExt = imageExtensions.includes(fileExtension)
    const isVideoByExt = videoExtensions.includes(fileExtension)
    if (!isImage && !isVideo && !isPdf && !isImageByExt && !isVideoByExt) {
      return `File "${file.name}" is not a supported image, video, or PDF format`
    }
    const MAX_IMAGE_SIZE = 50 * 1024 * 1024
    const MAX_VIDEO_SIZE = 500 * 1024 * 1024
    if ((isImage || isImageByExt) && file.size > MAX_IMAGE_SIZE) {
      return `Image "${file.name}" is too large. Maximum size is 50MB`
    }
    if ((isVideo || isVideoByExt) && file.size > MAX_VIDEO_SIZE) {
      return `Video "${file.name}" is too large. Maximum size is 500MB`
    }
    return null
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (files) {
      const fileArray = Array.from(files)
      const validFiles: File[] = []
      const errors: string[] = []
      const currentTotal = selectedFiles.reduce((s, f) => s + f.size, 0)
      const MAX_TOTAL = 1024 * 1024 * 1024

      fileArray.forEach((file) => {
        if (currentTotal + file.size > MAX_TOTAL) {
          errors.push('Total file size would exceed 1GB limit')
          return
        }
        const err = validateFile(file)
        if (err) errors.push(err)
        else validFiles.push(file)
      })
      if (errors.length > 0) errors.forEach((e) => errorToast(e))
      if (validFiles.length > 0) setSelectedFiles((prev) => [...prev, ...validFiles])
      event.target.value = ''
    }
  }

  const handleSubmit = async () => {
    if (submittedRef.current) return
    if (selectedFiles.length === 0) {
      errorToast('Please select at least one photo or video')
      return
    }
    try {
      submittedRef.current = true

      // Enqueue base photos in background
      await enqueueBasePhotos(
        regionId,
        selectedFiles,
        componentName,
        showDescription ? description.trim() || undefined : undefined
      )

      successToast(
        `Queued ${selectedFiles.length} photo(s) for background upload. You can close this modal or monitor progress in the navigation bar.`
      )

      setSelectedFiles([])
      setDescription('')
      if (onUploadSuccess) onUploadSuccess()
      onClose()
    } catch (error) {
      console.error('Error enqueuing base photos:', error)
      errorToast(error instanceof Error ? error.message : 'An error occurred while queueing photos. Please try again.')
    } finally {
      submittedRef.current = false
    }
  }

  const handleCancel = () => {
    setSelectedFiles([])
    setDescription('')
    onClose()
  }

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index))
  }

  const handleVoiceSave = async (audioBlob: Blob) => {
    setIsVoiceModalOpen(false)
    const f = new File([audioBlob], 'recording.webm', { type: audioBlob.type || 'audio/webm' })
    const formData = new FormData()
    formData.append('audio', f)
    try {
      const res = await authFetch('/ai/transcribe', { method: 'POST', body: formData, credentials: 'include' })
      const data = await res.json().catch(() => ({}))
      if (res.ok && data.success && data.data?.transcription) {
        const transcript = String(data.data.transcription)
        setDescription((prev) => (prev.trim() ? `${prev} ${transcript}` : transcript))
      } else {
        errorToast('message' in data ? data.message : 'Transcription failed')
      }
    } catch (e) {
      errorToast(e instanceof Error ? e.message : 'Transcription error')
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleCancel()}>
      <DialogContent className="sm:max-w-[540px] max-h-[90dvh] flex flex-col gap-4 overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>Add Photos</DialogTitle>
          <DialogDescription>
            Select photos or videos to add. You can add multiple files at once.
          </DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain space-y-4 [-webkit-overflow-scrolling:touch]">
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => galleryInputRef.current?.click()}
              className="flex w-full flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-gray-300 p-6 text-gray-500 transition-colors hover:border-gray-400 hover:bg-gray-50"
            >
              <span className="text-2xl leading-none">+</span>
              <span className="font-semibold">Click to upload</span>
              <span className="text-sm">Select Photos</span>
            </button>
            <input
              ref={galleryInputRef}
              type="file"
              accept="image/*,video/*,.pdf,application/pdf"
              multiple
              onChange={handleFileChange}
              className="hidden"
            />
          </div>

          {selectedFiles.length > 0 && (
            <div className="space-y-2">
              <Label>Selected Media ({selectedFiles.length})</Label>
              <div className="grid grid-cols-3 gap-2 p-2 max-h-48 sm:max-h-64 overflow-y-auto overflow-x-hidden overscroll-contain [-webkit-overflow-scrolling:touch] scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                {selectedFiles.map((file, i) => (
                  <FilePreviewItem
                    key={`${file.name}-${i}`}
                    file={file}
                    isVideo={file.type.startsWith('video/')}
                    isPdf={
                      file.type === 'application/pdf' ||
                      file.name.toLowerCase().endsWith('.pdf')
                    }
                    onRemove={() => removeFile(i)}
                  />
                ))}
              </div>
            </div>
          )}

          {showDescription && (
            <div>
              <div className="flex items-center justify-between gap-2 mb-1">
                <Label htmlFor="base-photo-desc">Description (optional)</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsVoiceModalOpen(true)}
                  className="gap-1.5"
                >
                  <Mic className="w-4 h-4" /> Voice
                </Button>
              </div>
              <Textarea
                id="base-photo-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Type or use Voice to add a description..."
                className="mt-1"
                rows={3}
              />
            </div>
          )}
          {showDescription && isVoiceModalOpen && (
            <VoiceRecordingModal
              isOpen
              onClose={() => setIsVoiceModalOpen(false)}
              onSave={handleVoiceSave}
              autoStart
            />
          )}
        </div>
        <DialogFooter className="shrink-0 gap-2">
          <Button variant="outline" onClick={handleCancel} className="w-full">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={selectedFiles.length === 0}
            className="w-full"
          >
            Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
